import { Link } from "react-router-dom";
import {
  ArrowRight,
  MessageCircle,
  ShieldCheck,
  Clock,
  CheckCircle2,
  Phone,
  Quote,
} from "lucide-react";
import { siteConfig } from "../config/siteConfig";
import ServiceIcon from "../components/ServiceIcon";

export const Home = () => {
  const { home, contact, services } = siteConfig;
  const waUrl = `https://wa.me/${contact.whatsappRaw}?text=${encodeURIComponent(
    contact.whatsappMessage,
  )}`;

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section
        className="relative overflow-hidden grain-overlay"
        style={{
          backgroundImage:
            "linear-gradient(rgba(15,42,74,0.85), rgba(15,42,74,0.75)), url('/images/hero.jpg')",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
        data-testid="hero-section"
      >
        <div className="container-site py-24 md:py-36 relative z-10">
          <div className="max-w-3xl fade-up">
            <span className="inline-flex items-center gap-2 px-4 py-1.5 bg-red-600 text-white text-xs font-bold rounded-full pulse-emergency mb-6">
              <span className="w-1.5 h-1.5 bg-white rounded-full" />
              {home.hero.eyebrow}
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white leading-[1.05] tracking-tight">
              {home.hero.title}
            </h1>
            <p className="mt-6 text-lg text-slate-200 leading-relaxed max-w-2xl">
              {home.hero.subtitle}
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <a
                href={waUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-green-600 hover:bg-green-600 text-white font-bold rounded-sm transition shadow-lg hover:shadow-xl"
                data-testid="hero-whatsapp-cta"
              >
                <MessageCircle className="w-5 h-5" />
                {home.hero.ctaPrimary}
              </a>
              <Link
                to="/servicos"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white/10 hover:bg-white/20 backdrop-blur text-white font-bold rounded-sm border border-white/30 transition"
                data-testid="hero-services-cta"
              >
                {home.hero.ctaSecondary}
                <ArrowRight className="w-5 h-5" />
              </Link>
            </div>

            <div className="mt-10 flex items-center gap-3 text-slate-300 text-sm">
              <Phone className="w-4 h-4 text-green-400" />
              Ou ligue agora:
              <a
                href={`tel:${contact.phoneRaw}`}
                className="font-bold text-white hover:text-green-400"
              >
                {contact.phone}
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* TRUST BAR */}
      <section className="bg-[#f8fafc] border-y border-slate-200">
        <div className="container-site py-10 grid grid-cols-2 md:grid-cols-4 gap-8">
          {home.trustBar.map((item) => (
            <div
              key={item.label}
              className="text-center md:text-left"
              data-testid={`trust-${item.label.toLowerCase()}`}
            >
              <div className="text-3xl md:text-4xl font-black text-[#0f2a4a] tracking-tight">
                {item.value}
              </div>
              <div className="text-xs tracking-[0.2em] uppercase font-bold text-slate-500 mt-1">
                {item.label}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* SERVICES SNIPPET */}
      <section className="py-20 md:py-28">
        <div className="container-site">
          <div className="flex flex-col md:flex-row justify-between md:items-end mb-12 gap-6">
            <div className="max-w-2xl">
              <div className="eyebrow mb-3">Serviços</div>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-black tracking-tight">
                Soluções completas em desentupimento
              </h2>
              <p className="mt-4 text-slate-600 leading-relaxed">
                Atuamos em residências, condomínios e empresas com equipamentos
                modernos e equipe treinada.
              </p>
            </div>
            <Link
              to="/servicos"
              className="inline-flex items-center gap-2 text-[#0f2a4a] font-bold hover:text-blue-700 transition self-start"
              data-testid="home-services-link"
            >
              Ver todos os serviços <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {services.slice(0, 4).map((s) => (
              <div
                key={s.title}
                className="group bg-white border border-slate-200 p-6 rounded-sm hover:-translate-y-1 hover:shadow-xl hover:border-green-300 transition-all duration-300"
                data-testid={`service-card-${s.title}`}
              >
                <div className="inline-flex items-center justify-center w-12 h-12 bg-[#0f2a4a] text-green-400 rounded-sm mb-5 group-hover:bg-green-600 group-hover:text-white transition">
                  <ServiceIcon name={s.icon} className="w-6 h-6" />
                </div>
                <h3 className="text-lg font-bold text-[#0f2a4a] mb-2">
                  {s.title}
                </h3>
                <p className="text-sm text-slate-600 leading-relaxed">
                  {s.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY US */}
      <section className="py-20 md:py-28 bg-[#f8fafc]">
        <div className="container-site">
          <div className="max-w-2xl mb-14">
            <div className="eyebrow mb-3">Por que nos escolher</div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-black tracking-tight">
              Profissionais que você pode chamar a qualquer hora
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {home.whyUs.map((w, i) => (
              <div
                key={w.title}
                className="bg-white border border-slate-200 p-8 rounded-sm hover:shadow-lg transition"
                data-testid={`why-${i}`}
              >
                <div className="flex items-start gap-4">
                  <CheckCircle2 className="w-6 h-6 text-blue-700 shrink-0 mt-1" />
                  <div>
                    <h3 className="text-xl font-bold text-[#0f2a4a]">
                      {w.title}
                    </h3>
                    <p className="mt-2 text-slate-600 leading-relaxed">
                      {w.desc}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-20 md:py-28">
        <div className="container-site">
          <div className="max-w-2xl mb-14">
            <div className="eyebrow mb-3">Depoimentos</div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-black tracking-tight">
              Clientes que confiam na Dr. Pipe
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {home.testimonials.map((t, i) => (
              <div
                key={i}
                className="bg-white border border-slate-200 p-8 rounded-sm hover:shadow-lg transition relative"
                data-testid={`testimonial-${i}`}
              >
                <Quote className="w-8 h-8 text-blue-700/20 mb-4" />
                <p className="text-slate-700 leading-relaxed">"{t.text}"</p>
                <div className="mt-6 pt-5 border-t border-slate-100">
                  <div className="font-bold text-[#0f2a4a]">{t.name}</div>
                  <div className="text-xs text-slate-500 mt-0.5">{t.role}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="bg-[#0f2a4a] text-white" data-testid="final-cta">
        <div className="container-site py-20 md:py-28 grid md:grid-cols-[1fr_auto] gap-10 items-center">
          <div className="max-w-2xl">
            <div className="eyebrow text-green-400 mb-3">
              Atendimento imediato
            </div>
            <h2 className="text-3xl md:text-5xl font-black text-white tracking-tight">
              Entupimento na sua casa ou empresa?
              <br />
              Chame a Dr. Pipe agora.
            </h2>
            <p className="mt-5 text-slate-300 leading-relaxed">
              Orçamento gratuito, chegada rápida e 90 dias de garantia.
            </p>
          </div>
          <div className="flex flex-col gap-3 min-w-[280px]">
            <a
              href={waUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-green-600 hover:bg-green-700 text-white font-bold rounded-sm transition shadow-lg"
              data-testid="final-whatsapp-cta"
            >
              <MessageCircle className="w-5 h-5" />
              Falar no WhatsApp
            </a>
            <a
              href={`tel:${contact.phoneRaw}`}
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white/10 hover:bg-white/20 text-white font-bold rounded-sm border border-white/30 transition"
              data-testid="final-phone-cta"
            >
              <Phone className="w-5 h-5" />
              {contact.phone}
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
